"use client"

import {
  BarChart as RBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList, Cell, Brush, ReferenceLine, ErrorBar,
} from "recharts"
import {
  type BaseChartProps, DEFAULT_PALETTE, inferSeries, colorFor,
} from "./types"
import type { ReactElement } from "react"

export interface BarSeriesConfig {
  /** Series name (matches the data field). */
  name: string
  color?: string
  /** Field name in each row that holds an error-bar magnitude. */
  errorKey?: string
  /** Show value labels on each bar. */
  showLabels?: boolean
  /** Stack ID — bars sharing the same stackId are stacked together. */
  stackId?: string
  /** Custom corner radius for this series. */
  radius?: number
}

export interface BarChartProps extends BaseChartProps {
  /** Stack bars from the same row instead of grouping side-by-side. */
  stacked?: boolean
  /** Stack by sign — positives stack above zero, negatives below. */
  stackedBySign?: boolean
  /** Render bars horizontally (category on Y axis, value on X axis). */
  horizontal?: boolean
  /** Show data-value labels above each bar. */
  showValueLabels?: boolean
  /** Render bars with no fill (outlined / "ghost" bars). */
  outlined?: boolean
  /** Two Y axes — first half of series on left, rest on right. */
  biaxial?: boolean
  /** Background fill behind each bar (subtle "track" look). */
  withBackground?: boolean
  /** Override the default rounded-corner radius. */
  barRadius?: number
  /** Add a brush (range selector) below the chart. */
  withBrush?: boolean
  /** Per-row override of bar color (e.g. positive=green, negative=red). */
  colorByValueKey?: string  // field to inspect; when present uses palette[0]/[1] split by sign
  /** Reference horizontal lines (e.g. target threshold). */
  referenceLines?: { y: number; label?: string; color?: string }[]
  /** Pixel padding either side of X axis. */
  xPadding?: { left?: number; right?: number }
  /** Minimum drawn bar size in pixels (so tiny values stay visible). */
  minBarSize?: number
  /** Per-series overrides — color, error bars, labels, custom stack id. */
  seriesConfig?: Record<string, BarSeriesConfig>
  /** Sync this chart's tooltip with another chart that shares the same id. */
  syncId?: string
}

export function BarChart({
  data, series, xKey = "name", palette = DEFAULT_PALETTE, colorMap,
  showLegend = true, showGrid = true, showTooltip = true,
  xLabel, yLabel, compact, className, style, height = "100%", width = "100%",
  stacked, stackedBySign, horizontal, showValueLabels, outlined, biaxial, withBackground, barRadius = 3,
  withBrush, colorByValueKey, referenceLines, xPadding, minBarSize, seriesConfig, syncId,
}: BarChartProps) {
  const seriesList = series ?? inferSeries(data, xKey)
  const stackMode: "sign" | undefined = stackedBySign ? "sign" : undefined
  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RBarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        stackOffset={stackMode}
        syncId={syncId}
        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
      >
        {showGrid && !compact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        {!compact && (horizontal
          ? <XAxis type="number" tick={{ fontSize: 10.5 }} padding={xPadding} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} />
          : <XAxis dataKey={xKey} type="category" tick={{ fontSize: 10.5 }} padding={xPadding} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />)}
        {!compact && (horizontal
          ? <YAxis dataKey={xKey} type="category" tick={{ fontSize: 10.5 }} width={80} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />
          : <YAxis yAxisId="left" tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />)}
        {biaxial && !horizontal && !compact && (
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10.5 }} />
        )}
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {referenceLines?.map((r, i) => (
          <ReferenceLine key={`rl-${i}`} yAxisId="left" y={r.y} stroke={r.color || "#888"} strokeDasharray="4 4" label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {seriesList.map((name, i): ReactElement => {
          const cfg: Partial<BarSeriesConfig> = seriesConfig?.[name] || {}
          const c = cfg.color || colorFor(name, i, palette, colorMap)
          const stackId = cfg.stackId || (stacked || stackedBySign ? "a" : undefined)
          return (
            <Bar
              key={name}
              dataKey={name}
              yAxisId={biaxial && !horizontal ? (i < seriesList.length / 2 ? "left" : "right") : (horizontal ? undefined : "left")}
              stackId={stackId}
              fill={outlined ? "none" : c}
              stroke={c}
              strokeWidth={outlined ? 2 : 0}
              radius={[cfg.radius ?? barRadius, cfg.radius ?? barRadius, 0, 0]}
              background={withBackground ? { fill: "rgba(125,125,125,0.08)" } : undefined}
              minPointSize={minBarSize}
              isAnimationActive={false}
            >
              {colorByValueKey && data.map((row, idx) => {
                const v = Number(row[colorByValueKey]) || 0
                const fill = v >= 0 ? (palette[0] || "#10b981") : (palette[1] || "#ef4444")
                return <Cell key={`c-${idx}`} fill={fill} />
              })}
              {(cfg.showLabels || showValueLabels) && <LabelList dataKey={name} position={horizontal ? "right" : "top"} style={{ fontSize: 10 }} />}
              {cfg.errorKey && <ErrorBar dataKey={cfg.errorKey} stroke={c} strokeWidth={1.5} />}
            </Bar>
          )
        })}
        {withBrush && <Brush dataKey={xKey} height={20} stroke={palette[0]} />}
      </RBarChart>
    </ResponsiveContainer>
  )
}
